import {
  IApplicantOfferRequirement,
  IApplicantOfferAcademicRequirement,
  IApplicantOfferRequirementTask,
  ApplicantOfferRequirementTask,
} from 'types';

type BackendRequirement = IApplicantOfferRequirement | IApplicantOfferAcademicRequirement;

export interface TransformedRequirementTask extends IApplicantOfferRequirementTask {
  requirementId: string; // Unique per requirement group (qualification id for academic, enum for others)
  sectionId?: string; // Maps to ApplicantOfferRequirement enum for section-level grouping; falls back to requirementId
  requirementText?: string;
  isPrimaryAcademicRequirement?: boolean; // True for the main academic requirement in a multi-group setup
}

const academicTaskSortOrder: Partial<Record<ApplicantOfferRequirementTask, number>> = {
  [ApplicantOfferRequirementTask.AcademicResults]: 1,
  [ApplicantOfferRequirementTask.FinalAcademicResults]: 2,
  [ApplicantOfferRequirementTask.AcademicCertificate]: 3,
  [ApplicantOfferRequirementTask.AcademicTranscript]: 4,
};

const englishLanguageTaskSortOrder: Partial<Record<ApplicantOfferRequirementTask, number>> = {
  [ApplicantOfferRequirementTask.EnglishLanguageResults]: 1,
  [ApplicantOfferRequirementTask.EnglishLanguageCertificate]: 2,
};

export function sortAcademicTasks(tasks: Array<TransformedRequirementTask>): Array<TransformedRequirementTask> {
  return [...tasks].sort((a, b) => {
    const aOrder = academicTaskSortOrder[a.type as ApplicantOfferRequirementTask];
    const bOrder = academicTaskSortOrder[b.type as ApplicantOfferRequirementTask];

    // If both are academic tasks, sort by order
    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }

    // If only one is an academic task, prioritize it
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;

    // Otherwise maintain original order
    return 0;
  });
}

export function sortEnglishLanguageTasks(tasks: Array<TransformedRequirementTask>): Array<TransformedRequirementTask> {
  return [...tasks].sort((a, b) => {
    const aOrder = englishLanguageTaskSortOrder[a.type as ApplicantOfferRequirementTask];
    const bOrder = englishLanguageTaskSortOrder[b.type as ApplicantOfferRequirementTask];

    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }

    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;

    return 0;
  });
}

function isAcademicRequirement(requirement: BackendRequirement): requirement is IApplicantOfferAcademicRequirement {
  return 'qualifications' in requirement;
}

export function transformRequirements(requirements: BackendRequirement[]): Array<TransformedRequirementTask> {
  const transformedTasks: Array<TransformedRequirementTask> = [];

  for (const requirement of requirements) {
    // Handle IApplicantOfferAcademicRequirement (has qualifications)
    if (isAcademicRequirement(requirement)) {
      const qualifications = Object.values(requirement.qualifications);
      for (const qualification of qualifications) {
        for (const [taskId, task] of Object.entries(qualification.tasks)) {
          transformedTasks.push({
            ...task,
            id: task.id ?? taskId,
            // Use qualification id as the grouping key so each academic requirement renders separately
            requirementId: qualification.id,
            sectionId: requirement.id, // Always maps to the ApplicantOfferRequirement enum for section grouping
            requirementText: qualification.requirementText ?? requirement.requirementText,
            isPrimaryAcademicRequirement: qualification.primary,
          });
        }
      }
      continue;
    }

    // Handle IApplicantOfferRequirement (has tasks directly)
    if ('tasks' in requirement) {
      for (const [taskId, task] of Object.entries(requirement.tasks)) {
        // StaffOnly tasks have parent IDs like "StaffOnly-abc123" (one per task), not the fixed
        // "StaffOnly" enum. Pin sectionId to "StaffOnly" so all staff tasks group under one section.
        const sectionId = task.type === ApplicantOfferRequirementTask.StaffOnly ? 'StaffOnly' : requirement.id;
        transformedTasks.push({
          ...task,
          id: task.id ?? taskId,
          requirementId: requirement.id,
          sectionId,
          requirementText: requirement.requirementText,
        });
      }
    }
  }

  return transformedTasks;
}

export interface RequirementGroup {
  requirementId: string;
  requirementText?: string;
  tasks: Array<TransformedRequirementTask>;
  isPrimaryAcademicRequirement?: boolean; // True for the main academic requirement group
}

export interface SectionGroup {
  sectionName: string;
  sectionPath: string;
  requirementGroups: Array<RequirementGroup>;
}

export function groupRequirementsBySection(
  tasks: Array<TransformedRequirementTask>,
  groupKey?: string,
): Array<RequirementGroup> {
  const grouped: Record<string, RequirementGroup> = {};

  for (const task of tasks) {
    const key = groupKey ?? task.requirementId;
    if (!grouped[key]) {
      grouped[key] = {
        requirementId: key,
        requirementText: task.requirementText,
        tasks: [],
        isPrimaryAcademicRequirement: task.isPrimaryAcademicRequirement,
      };
    }
    grouped[key].tasks.push(task);
  }

  // Sort tasks within each group to ensure academic tasks are in the correct order
  const result = Object.values(grouped);
  for (const group of result) {
    group.tasks = sortEnglishLanguageTasks(sortAcademicTasks(group.tasks));
  }

  // Sort groups so primary academic requirements appear before secondary ones
  result.sort((a, b) => {
    const aIsPrimary = a.isPrimaryAcademicRequirement !== false; // default true for legacy
    const bIsPrimary = b.isPrimaryAcademicRequirement !== false;
    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return 0;
  });

  return result;
}

export function getAllTasksFromRequirements(
  requirements: BackendRequirement[] | undefined | null,
): IApplicantOfferRequirementTask[] {
  if (!requirements || !Array.isArray(requirements)) {
    return [];
  }

  const tasks: IApplicantOfferRequirementTask[] = [];

  for (const requirement of requirements) {
    if (!requirement) continue;

    if (isAcademicRequirement(requirement)) {
      const qualifications = Object.values(requirement.qualifications ?? {});
      for (const qualification of qualifications) {
        if (qualification?.tasks) {
          tasks.push(...Object.values(qualification.tasks).filter(Boolean));
        }
      }
    } else if ('tasks' in requirement && requirement.tasks) {
      tasks.push(...Object.values(requirement.tasks).filter(Boolean));
    }
  }

  return tasks;
}

export function updateTaskInRequirements(
  requirements: BackendRequirement[],
  updatedTask: IApplicantOfferRequirementTask,
): BackendRequirement[] {
  return requirements.map(requirement => {
    if (isAcademicRequirement(requirement)) {
      // Check if any qualification contains this task
      const newQualifications = { ...requirement.qualifications };
      let found = false;

      for (const [qualId, qualification] of Object.entries(newQualifications)) {
        if (qualification.tasks[updatedTask.id]) {
          newQualifications[qualId] = {
            ...qualification,
            tasks: {
              ...qualification.tasks,
              [updatedTask.id]: updatedTask,
            },
          };
          found = true;
          break;
        }
      }

      if (found) {
        return {
          ...requirement,
          qualifications: newQualifications,
        };
      }
    } else if ('tasks' in requirement && requirement.tasks[updatedTask.id]) {
      return {
        ...requirement,
        tasks: {
          ...requirement.tasks,
          [updatedTask.id]: updatedTask,
        },
      };
    }

    return requirement;
  });
}
